/*
  Radman ads landing page: cover finder
  No dependencies. Loaded with defer by sections/ads-finder.liquid.
  Same flow as the homepage size finder: pick a size, pick a format, see covers.

  The URL hash is the single source of truth:
    #find                          nothing chosen
    #find/<size>                   size chosen, format buttons enabled
    #find/<size>/<format>          results (format: cut, roll, handheld, zip, all)
    #find/custom-15x20[/<format>]  "Other size", width x height in inches
  Anything that does not validate falls back to the deepest valid state.
  Hashes that do not start with #find (for example #get-pricing) are ignored,
  so jumping to the lead form never resets the finder.

  Every change dispatches a DOM event on document:
    document.addEventListener('rr:finder', function (e) { console.log(e.detail); });
  detail = { action, step, size, sizeLabel, format, formatLabel, custom,
             products, count, product, reason }
  actions: size, format, results, nomatch, custom_open, result_cta,
           nomatch_cta, restart
  If the section setting "push to dataLayer" is on, the same detail is pushed
  as { event: 'rr_finder_<action>', rr_finder: detail }.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-rr-finder]';
  var FORMATS = ['cut', 'roll', 'handheld', 'zip'];

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // "17 × 17 in" and '17" × 17" cassette' both become [17, 17] (smaller side first).
  function dims(text) {
    var m = String(text || '').match(/(\d+(?:\.\d+)?)[^\d×x]{0,4}[×x]\s*(\d+(?:\.\d+)?)/);
    if (!m) return null;
    var a = parseFloat(m[1]);
    var b = parseFloat(m[2]);
    return [Math.min(a, b), Math.max(a, b)];
  }

  function dimsKey(text) {
    var d = dims(text);
    return d ? d[0] + 'x' + d[1] : '';
  }

  function fmtNum(n) {
    return String(Math.round(n * 10) / 10);
  }

  function Finder(root) {
    var node = root.querySelector('[data-rrf-data]');
    if (!node) return;
    try { this.data = JSON.parse(node.textContent); } catch (e) { return; }

    var self = this;
    this.root = root;
    this.formAnchor = root.getAttribute('data-form-anchor') || '#get-pricing';
    this.useDataLayer = root.getAttribute('data-datalayer') === 'true';
    this.live = root.querySelector('[data-rrf-live]');
    this.sizeChips = Array.prototype.slice.call(root.querySelectorAll('button[data-rrf-size]'));
    this.formatChips = Array.prototype.slice.call(root.querySelectorAll('button[data-rrf-format]'));
    this.formatStep = root.querySelector('[data-rrf-step="format"]');
    this.resultsStep = root.querySelector('[data-rrf-step="results"]');
    this.nomatchStep = root.querySelector('[data-rrf-step="nomatch"]');
    this.customPanel = root.querySelector('[data-rrf-custom]');
    this.customForm = root.querySelector('[data-rrf-custom-form]');
    this.customError = root.querySelector('[data-rrf-custom-error]');
    this.inputW = root.querySelector('[data-rrf-w]');
    this.inputH = root.querySelector('[data-rrf-h]');
    this.list = root.querySelector('[data-rrf-results]');
    this.cards = {};
    root.querySelectorAll('[data-rrf-product]').forEach(function (c) {
      self.cards[c.getAttribute('data-rrf-product')] = c;
    });
    this.sizes = {};
    (this.data.sizes || []).forEach(function (s) { self.sizes[s.key] = s; });
    this.state = { size: null, custom: null, format: null, customOpen: false };

    this.bind();
    this.route(location.hash, { initial: true });
  }

  /* ---------- data ---------- */

  Finder.prototype.sizeLabel = function (s) {
    s = s || this.state;
    if (s.custom) {
      return (this.data.text.customLabel || '{w} × {h} in')
        .replace('{w}', fmtNum(s.custom.w)).replace('{h}', fmtNum(s.custom.h));
    }
    return s.size && this.sizes[s.size] ? this.sizes[s.size].label : '';
  };

  Finder.prototype.formatLabel = function (f) {
    return (this.data.text.formats && this.data.text.formats[f]) || '';
  };

  // Width and height in inches for the current choice: the typed size, or the
  // preset button's own width and height.
  Finder.prototype.dimsFor = function (s) {
    if (s.custom) return { w: s.custom.w, h: s.custom.h };
    var size = this.sizes[s.size];
    if (!size) return null;
    var w = Number(size.w);
    var h = Number(size.h);
    return w > 0 && h > 0 ? { w: w, h: h } : null;
  };

  // Every product that fits the chosen size, before the format filter. Preset
  // buttons and typed sizes use the same geometry, so clicking 14 x 17 returns
  // exactly what typing 14 x 17 under "Other size" does.
  Finder.prototype.candidates = function (s) {
    var self = this;
    var d = this.dimsFor(s);
    if (!d) {
      var size = this.sizes[s.size];
      if (!size) return [];
      return (size.products || []).filter(function (h) { return !!self.cards[h]; });
    }
    var c = Number(this.data.clearance);
    if (isNaN(c)) c = 1;
    return Object.keys(this.cards).filter(function (handle) {
      var bag = dims(self.cards[handle].getAttribute('data-rrf-bag'));
      if (!bag) return false;
      var bw = bag[0];
      var bh = bag[1];
      return (bw >= d.w + c && bh >= d.h + c) || (bw >= d.h + c && bh >= d.w + c);
    }).sort(function (a, b) {
      var da = dims(self.cards[a].getAttribute('data-rrf-bag'));
      var db = dims(self.cards[b].getAttribute('data-rrf-bag'));
      return da[0] * da[1] - db[0] * db[1];
    });
  };

  Finder.prototype.formatsFor = function (handles) {
    var self = this;
    var has = {};
    handles.forEach(function (h) { has[self.cards[h].getAttribute('data-rrf-format')] = true; });
    return has;
  };

  Finder.prototype.results = function (s) {
    var self = this;
    var all = this.candidates(s);
    var want = s.custom ? '' : dimsKey(this.sizeLabel(s));
    var order = this.data.formatOrder || FORMATS;
    var best = {};
    var list = all.filter(function (h) {
      return s.format === 'all' || self.cards[h].getAttribute('data-rrf-format') === s.format;
    });
    list.forEach(function (h) {
      best[h] = !!want && dimsKey(self.cards[h].getAttribute('data-rrf-fit')) === want;
    });
    // Same cap for a preset button or a typed size: the closest fits.
    var limit = parseInt(this.data.customLimit, 10) || 6;
    list = list.slice(0, limit);
    if (!s.custom) {
      // Within those, the most common fit goes first; otherwise closest fit first.
      list = list.map(function (h, i) {
        return { h: h, i: i, b: best[h] ? 0 : 1 };
      }).sort(function (a, b) { return (a.b - b.b) || (a.i - b.i); })
        .map(function (x) { return x.h; });
    }
    return { products: list, best: best };
  };

  /* ---------- routing ---------- */

  Finder.prototype.parse = function (hash) {
    if (!hash || hash.charAt(0) !== '#') return null;
    var parts = hash.slice(1).split('/').map(function (p) {
      try { return decodeURIComponent(p); } catch (e) { return p; }
    });
    if (parts[0] !== 'find') return null;
    var s = { size: null, custom: null, format: null, customOpen: false };

    var size = parts[1];
    if (!size) return s;
    if (size === 'custom') {
      s.customOpen = true;
      return s;
    }
    var m = size.match(/^custom-(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/);
    if (m) {
      var w = parseFloat(m[1]);
      var h = parseFloat(m[2]);
      s.customOpen = true;
      if (!(w > 0 && h > 0 && w <= 60 && h <= 60)) return s;
      s.custom = { w: w, h: h };
    } else if (Object.prototype.hasOwnProperty.call(this.sizes, size)) {
      s.size = size;
    } else {
      return s;
    }

    var f = parts[2];
    if (f && (f === 'all' || FORMATS.indexOf(f) !== -1)) s.format = f;
    return s;
  };

  Finder.prototype.hashFor = function (s) {
    var parts = ['find'];
    if (s.custom) parts.push('custom-' + fmtNum(s.custom.w) + 'x' + fmtNum(s.custom.h));
    else if (s.size) parts.push(s.size);
    else if (s.customOpen) parts.push('custom');
    if ((s.size || s.custom) && s.format) parts.push(s.format);
    return '#' + parts.join('/');
  };

  Finder.prototype.go = function (s) {
    var h = this.hashFor(s);
    if (h === location.hash) this.route(h);
    else location.hash = h;
  };

  Finder.prototype.route = function (hash, opts) {
    opts = opts || {};
    var parsed = this.parse(hash);
    if (!parsed) {
      // Not a finder hash. An empty hash (back to the first entry) resets
      // quietly; anything else (e.g. #get-pricing) leaves the finder alone.
      if (hash && hash !== '#') return;
      parsed = { size: null, custom: null, format: null, customOpen: false };
      opts.quiet = true;
    }
    var prev = this.state;
    var h = this.hashFor(parsed);
    if (hash && hash.indexOf('#find') === 0 && hash !== h && history.replaceState) {
      history.replaceState(history.state, '', location.pathname + location.search + h);
    }
    this.state = parsed;
    this.render(prev, opts);
  };

  /* ---------- rendering ---------- */

  Finder.prototype.setPressed = function (chips, attr, value) {
    chips.forEach(function (b) {
      var on = b.getAttribute(attr) === value;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  Finder.prototype.render = function (prev, opts) {
    var self = this;
    var s = this.state;
    var hasSize = !!(s.size || s.custom);

    // Step 1
    this.setPressed(this.sizeChips, 'data-rrf-size', s.custom || s.customOpen ? 'custom' : (s.size || ''));
    var other = this.root.querySelector('button[data-rrf-size="custom"]');
    if (other) other.setAttribute('aria-expanded', s.customOpen ? 'true' : 'false');
    if (this.customPanel) this.customPanel.hidden = !s.customOpen;
    if (this.customError) this.customError.hidden = true;
    if (s.custom && this.inputW && this.inputH) {
      this.inputW.value = fmtNum(s.custom.w);
      this.inputH.value = fmtNum(s.custom.h);
    }

    // Step 2: enabled once there is a size; formats with no covers stay disabled
    var cands = hasSize ? this.candidates(s) : [];
    var has = this.formatsFor(cands);
    this.formatStep.classList.toggle('is-waiting', !hasSize);
    this.formatChips.forEach(function (b) {
      var f = b.getAttribute('data-rrf-format');
      b.disabled = !(hasSize && cands.length > 0 && (f === 'all' || !!has[f]));
    });
    this.setPressed(this.formatChips, 'data-rrf-format', hasSize ? (s.format || '') : '');

    // Results / no match
    var step = 'size';
    var announce = '';
    var r = null;
    if (hasSize && s.format) {
      r = this.results(s);
      step = r.products.length ? 'results' : 'nomatch';
    } else if (hasSize && cands.length === 0) {
      step = 'nomatch';
    } else if (hasSize) {
      step = 'format';
    }
    this.resultsStep.hidden = step !== 'results';
    this.nomatchStep.hidden = step !== 'nomatch';
    this.root.setAttribute('data-rr-current-step', step);

    if (step === 'results') {
      Object.keys(this.cards).forEach(function (h) { self.cards[h].hidden = true; });
      r.products.forEach(function (h) {
        var card = self.cards[h];
        card.hidden = false;
        var flag = card.querySelector('[data-rrf-best]');
        if (flag) flag.hidden = !r.best[h];
        card.classList.toggle('is-best', !!r.best[h]);
        self.list.appendChild(card);
      });
      // Build the heading so the size never breaks across lines ("14 ×" / "17 in").
      var title = this.resultsStep.querySelector('[data-rrf-results-title]');
      var parts = (title.getAttribute('data-template') || '').replace('{count}', r.products.length).split('{size}');
      title.textContent = parts[0];
      if (parts.length > 1) {
        var nb = document.createElement('span');
        nb.className = 'rr-nowrap';
        nb.textContent = this.sizeLabel();
        title.appendChild(nb);
        title.appendChild(document.createTextNode(parts.slice(1).join(this.sizeLabel())));
      }
      announce = (this.data.text.resultsLive || '')
        .replace('{count}', r.products.length).replace('{size}', this.sizeLabel());
    } else if (step === 'nomatch') {
      this.nomatchStep.querySelector('[data-rrf-summary]').textContent =
        (this.data.text.summaryLabel || '') + ' ' + this.selectionText();
      announce = this.data.text.nomatchLive || '';
    } else if (step === 'format') {
      announce = (this.data.text.sizeChosen || '').replace('{size}', this.sizeLabel());
    }

    // Events
    var sizeChanged = !prev || prev.size !== s.size || JSON.stringify(prev.custom) !== JSON.stringify(s.custom);
    var formatChanged = !prev || prev.format !== s.format;
    if (hasSize && sizeChanged) this.emit('size');
    if (hasSize && s.format && formatChanged) this.emit('format');
    if (step === 'results') this.emit('results', { products: r.products, count: r.products.length });
    if (step === 'nomatch') this.emit('nomatch');

    if (opts.quiet) return;
    if (opts.initial) {
      // Deep link: bring the finder into view, but do not move focus on load.
      if (location.hash.indexOf('#find/') === 0) this.root.scrollIntoView({ block: 'start' });
      return;
    }
    if (this.live) this.live.textContent = announce;

    // Bring the next thing into view if it is off screen (mostly on phones).
    var target = null;
    if (step === 'results') target = this.resultsStep;
    else if (step === 'nomatch') target = this.nomatchStep;
    else if (step === 'format' && sizeChanged) target = this.formatStep;
    if (target) {
      var rect = target.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top > vh * 0.75 || rect.top < 0) {
        target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }
  };

  Finder.prototype.selectionText = function () {
    var parts = [];
    var size = this.sizeLabel();
    if (size) parts.push(size);
    if (this.state.format) parts.push(this.formatLabel(this.state.format));
    return parts.join(' · ');
  };

  /* ---------- lead form hand-off ---------- */

  Finder.prototype.fillLead = function (info) {
    var form = document.querySelector('[data-rr-track="lead-form"]');
    if (!form) return false;
    var s = this.state;
    function set(name, value) {
      var f = form.querySelector('[data-rr-field="' + name + '"]');
      if (f) f.value = value || '';
    }
    var sizeText = this.sizeLabel();
    if (s.size && this.sizes[s.size] && this.sizes[s.size].alt) sizeText += ' (' + this.sizes[s.size].alt + ')';
    if (s.custom) sizeText += ' (custom size entered)';
    set('finder-size', sizeText);
    set('finder-format', s.format ? this.formatLabel(s.format) : '');
    set('finder-result', info.result);
    set('finder-link', location.origin + location.pathname + this.hashFor(s));

    if (info.productCode) {
      var interest = form.querySelector('[data-rr-field="product-interest"]');
      if (interest && interest.value.indexOf(info.productCode) === -1) {
        interest.value = interest.value ? interest.value + '; ' + info.productTitle : info.productTitle;
      }
    }

    var summary = form.querySelector('[data-rr-finder-summary]');
    if (summary) {
      var text = summary.querySelector('[data-rr-finder-summary-text]');
      var sel = this.selectionText();
      if (info.productTitle) sel += (sel ? ' · ' : '') + info.productTitle;
      if (text) text.textContent = sel;
      summary.hidden = !sel;
    }

    form.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    var toFocus = Array.prototype.filter.call(form.querySelectorAll('input[required]'), function (i) { return !i.value; })[0]
      || form.querySelector('input:not([type="hidden"])');
    if (toFocus) {
      try { toFocus.focus({ preventScroll: true }); } catch (e) { toFocus.focus(); }
    }
    return true;
  };

  /* ---------- events ---------- */

  Finder.prototype.emit = function (action, extra) {
    var s = this.state;
    var detail = {
      action: action,
      step: this.root.getAttribute('data-rr-current-step'),
      size: s.custom ? 'custom' : (s.size || ''),
      sizeLabel: this.sizeLabel(),
      custom: s.custom ? { w: s.custom.w, h: s.custom.h } : null,
      format: s.format || '',
      formatLabel: s.format ? this.formatLabel(s.format) : ''
    };
    if (extra) Object.keys(extra).forEach(function (k) { detail[k] = extra[k]; });
    try { document.dispatchEvent(new CustomEvent('rr:finder', { detail: detail })); } catch (e) { /* old browsers */ }
    if (this.useDataLayer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'rr_finder_' + action, rr_finder: detail });
    }
  };

  Finder.prototype.bind = function () {
    var self = this;

    window.addEventListener('hashchange', function () { self.route(location.hash); });

    this.root.addEventListener('click', function (e) {
      var el = e.target.closest('button[data-rrf-size], button[data-rrf-format], a[data-rr-track]');
      if (!el || !self.root.contains(el)) return;
      var s = self.state;

      if (el.tagName === 'BUTTON' && el.hasAttribute('data-rrf-size')) {
        var key = el.getAttribute('data-rrf-size');
        if (key === 'custom') {
          self.emit('custom_open');
          self.go({ size: null, custom: null, format: null, customOpen: true });
          if (self.customPanel) self.customPanel.hidden = false;
          if (self.inputW) {
            try { self.inputW.focus({ preventScroll: true }); } catch (err) { self.inputW.focus(); }
          }
          return;
        }
        // Keep the chosen format if the new size has covers in it.
        var next = { size: key, custom: null, format: s.format, customOpen: false };
        if (next.format && next.format !== 'all') {
          var has = self.formatsFor(self.candidates(next));
          if (!has[next.format]) next.format = null;
        }
        self.go(next);
        return;
      }

      if (el.tagName === 'BUTTON' && el.hasAttribute('data-rrf-format')) {
        if (el.disabled) return;
        self.go({ size: s.size, custom: s.custom, format: el.getAttribute('data-rrf-format'), customOpen: !!s.custom });
        return;
      }

      var track = el.getAttribute('data-rr-track');
      if (track === 'finder-restart') {
        e.preventDefault();
        self.emit('restart');
        if (self.inputW) self.inputW.value = '';
        if (self.inputH) self.inputH.value = '';
        self.go({ size: null, custom: null, format: null, customOpen: false });
        var first = self.sizeChips[0];
        if (first) {
          try { first.focus({ preventScroll: true }); } catch (err) { first.focus(); }
          self.root.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        }
        return;
      }

      if (track === 'finder-result-cta') {
        self.emit('result_cta', { product: el.getAttribute('data-rr-product') });
        if (self.fillLead({
          result: 'Matched: ' + el.getAttribute('data-rr-product-code'),
          productCode: el.getAttribute('data-rr-product-code'),
          productTitle: el.getAttribute('data-rr-product-title')
        })) e.preventDefault();
        return;
      }

      if (track === 'finder-nomatch-cta') {
        var reason = el.getAttribute('data-rr-reason') || 'no-match';
        self.emit('nomatch_cta', { reason: reason });
        var results = { 'no-match': 'No match', 'not-sure-after-results': 'Asked to confirm fit', 'custom-size': 'Asked about a custom size' };
        if (self.fillLead({
          result: results[reason] || 'Asked to confirm fit',
          productCode: reason === 'custom-size' ? 'Custom size' : '',
          productTitle: reason === 'custom-size' ? 'Custom size' : ''
        })) e.preventDefault();
      }
    });

    if (this.customForm) {
      this.customForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var w = parseFloat(self.inputW.value);
        var h = parseFloat(self.inputH.value);
        var wOk = w > 0 && w <= 60;
        var hOk = h > 0 && h <= 60;
        self.inputW.setAttribute('aria-invalid', wOk ? 'false' : 'true');
        self.inputH.setAttribute('aria-invalid', hOk ? 'false' : 'true');
        if (!wOk || !hOk) {
          self.customError.hidden = false;
          (wOk ? self.inputH : self.inputW).focus();
          return;
        }
        self.customError.hidden = true;
        self.go({ size: null, custom: { w: w, h: h }, format: self.state.format, customOpen: true });
      });
    }
  };

  ready(function () {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (root.__rrFinder) return;
      root.__rrFinder = new Finder(root);
    });

    // The main menu's "Find Your Size" points at the homepage finder. On a page
    // with this finder, keep visitors here and send it to #find instead.
    if (document.querySelector(ROOT_SELECTOR)) {
      document.querySelectorAll('a[href$="#find-your-size"]').forEach(function (a) {
        a.setAttribute('href', '#find');
      });
    }
  });

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector(ROOT_SELECTOR);
    if (root && !root.__rrFinder) root.__rrFinder = new Finder(root);
  });
})();
